import React from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { ShieldCheck, Building2, Mail, Cloud, LogOut, Users, Megaphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

import InviteManagementPage  from './InviteManagementPage';
import SocietyManagementPage from './SocietyManagementPage';
import SocietyDetailPage     from './SocietyDetailPage';
import SocietyDataPage       from './SocietyDataPage';
import CloudUsagePage        from './CloudUsagePage';
import LeadManagementPage    from './LeadManagementPage';
import MetaAdsPage           from './MetaAdsPage';

/* ── Nav items ───────────────────────────────────────────────────────────── */

const NAV = [
  { to: '/admin/societies',    label: 'Societies',   icon: Building2 },
  { to: '/admin/invites',      label: 'Invites',      icon: Mail      },
  { to: '/admin/leads',        label: 'Leads',        icon: Users     },
  { to: '/admin/meta-ads',     label: 'Meta Ads',     icon: Megaphone },
  { to: '/admin/cloud-usage',  label: 'Cloud Usage',  icon: Cloud     },
];

/* ── Admin home ──────────────────────────────────────────────────────────── */

const AdminHome: React.FC = () => (
  <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>
    <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 8 }}>
      Super Admin Panel
    </h1>
    <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
      Select a section from the sidebar to manage the Nexora platform.
    </p>
  </div>
);

/* ── AdminPage ───────────────────────────────────────────────────────────── */

const AdminPage: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: 228,
        background: '#0D1526',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
      }}>

        {/* Logo */}
        <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldCheck size={16} style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13, color: '#fff', letterSpacing: '-0.01em' }}>
                NEXORA
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', marginTop: 1 }}>
                SUPER ADMIN
              </div>
            </div>
          </div>

          {/* Signed-in user */}
          <div style={{ marginTop: 14, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.05)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: 1 }}>
              {user?.fullName}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '6px 10px 8px' }}>
            Platform
          </div>
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 8,
                marginBottom: 2,
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
                color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
                background: isActive
                  ? 'linear-gradient(135deg, rgba(26,108,246,0.22), rgba(109,40,217,0.12))'
                  : 'transparent',
                borderLeft: isActive ? '2px solid #1A6CF6' : '2px solid transparent',
                transition: 'all 150ms',
              })}
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => logout()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '9px 12px',
              background: 'none',
              border: 'none',
              borderRadius: 8,
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-body)',
              transition: 'color 150ms',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Content ── */}
      <main style={{ flex: 1, overflowY: 'auto', background: '#f8fafc' }}>
        <Routes>
          <Route index                element={<AdminHome />} />
          <Route path="societies"     element={<SocietyManagementPage />} />
          <Route path="societies/:id" element={<SocietyDetailPage />} />
          <Route path="societies/:id/data" element={<SocietyDataPage />} />
          <Route path="invites"       element={<InviteManagementPage />} />
          <Route path="leads"         element={<LeadManagementPage />} />
          <Route path="meta-ads"      element={<MetaAdsPage />} />
          <Route path="cloud-usage"   element={<CloudUsagePage />} />
          <Route path="*"             element={<AdminHome />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminPage;
